import { useMemo, useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useSnackbar } from 'notistack'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material'

import { useListTrustedActorsQuery } from '@/core/services/establishment'
import { useUpdatePatientMutation } from '@/core/services/patient'
import { useIngestExternalIdentityMutation } from '@/core/services/establishment'
import { useIsINSEnabled } from '@/shared/model/useIsINSEnabled'
import type { PatientProfileFormData } from '@/features/patient/patient-profile-form'
import { useRunInsVerification } from '@/features/patient/patient-profile-form/model/useRunInsVerification'
import { hasValidInsControlKey, hasValidInsFormat } from '@/shared/lib/ins-search'
import { buildExternalIdentityData, type ExternalIdentityPatientData } from '../lib/external-identity'
import { buildInsOnlyUpdateDto, getUpdateBlocker, isInsAlreadyVerifiedLocally, type UpdateBlocker } from '../lib/identity-matching'
import { useExternalIdentityReconciliation, type ReconciliationCandidate } from '../model/useExternalIdentityReconciliation'
import { CREATE_INSTEAD, ExternalIdentityReconciliationStep } from './ExternalIdentityReconciliationStep'

const UNTRUSTED_EMITTER = '__untrusted__'
const UNTRUSTED_EMITTER_NAME = 'Issuer without a trust agreement'

type Props = {
  open: boolean
  onClose: () => void
  establishmentId?: string
  onVerified?: (info: Partial<PatientProfileFormData>) => void
  onUpdated?: (patientId: string) => void
}

const BIRTH_PLACE_CODE_REGEX = /^[A-Z0-9]{5}$/i
const normalizeUppercaseInput = (value: string) => value.toUpperCase()
const INS_OID_OPTIONS = [
  { value: '1.2.250.1.213.1.4.8', label: 'NIR - 1.2.250.1.213.1.4.8' },
  { value: '1.2.250.1.213.1.4.9', label: 'NIA - 1.2.250.1.213.1.4.9' },
] as const
const REQUIRED_FIELD_SX = {
  '& .MuiFormLabel-asterisk': {
    display: 'none',
  },
  '& .MuiFormHelperText-root': {
    lineHeight: 1.35,
    minHeight: 34,
    marginTop: 0.25,
    whiteSpace: 'normal',
  },
} as const

export function IngestExternalIdentityDialog({ open, onClose, establishmentId, onVerified, onUpdated }: Props) {
  useLingui()
  const { enqueueSnackbar } = useSnackbar()

  const [step, setStep] = useState<'form' | 'reconciliation'>('form')
  const [candidates, setCandidates] = useState<ReconciliationCandidate[]>([])
  const [collisionPatient, setCollisionPatient] = useState<ReconciliationCandidate['patient'] | null>(null)
  const [selectedId, setSelectedId] = useState('')
  const [receivedData, setReceivedData] = useState<ExternalIdentityPatientData | null>(null)
  const [refusal, setRefusal] = useState<'KO' | 'ERROR' | null>(null)
  const [skippedReason, setSkippedReason] = useState<'ALREADY_VERIFIED' | 'COLLISION' | null>(null)
  const [searchFailed, setSearchFailed] = useState(false)

  const [emitter, setEmitter] = useState('')
  const [insNIR, setInsNIR] = useState('')
  const [insOID, setInsOID] = useState('')
  const [birthSurname, setBirthSurname] = useState('')
  const [birthName, setBirthName] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [sex, setSex] = useState('')
  const [birthPlaceCode, setBirthPlaceCode] = useState('')

  const { data: trustedActorsData } = useListTrustedActorsQuery({ establishmentId: establishmentId ?? '', limit: 200, skip: 0 }, { skip: !establishmentId })
  const trustedActors = trustedActorsData?.items ?? []
  const isTrustedEmitter = emitter !== '' && emitter !== UNTRUSTED_EMITTER && trustedActors.some((a) => a.name === emitter)
  const emitterName = isTrustedEmitter ? emitter : UNTRUSTED_EMITTER_NAME
  const { canRunInsVerification, isRunningInsVerification, runInsVerification } = useRunInsVerification()
  const { reconcile, isReconciling } = useExternalIdentityReconciliation()
  const [updatePatient, { isLoading: isUpdating }] = useUpdatePatientMutation()
  const [ingestExternalIdentity, { isLoading: isQuarantining }] = useIngestExternalIdentityMutation()

  const selectedCandidate = candidates.find((c) => c.patient._id === selectedId) ?? null
  const { isInsCallBlocked } = useIsINSEnabled(selectedCandidate?.patient)
  const blocker: UpdateBlocker | null =
    selectedCandidate && receivedData
      ? getUpdateBlocker(receivedData, selectedCandidate.patient, { collisionPatient, isInsCallBlocked, canRunInsVerification })
      : null
  const isBusy = isReconciling || isRunningInsVerification || isUpdating || isQuarantining

  const errors = useMemo(() => {
    const e: {
      insNIR?: string
      insOID?: string
      birthSurname?: string
      birthName?: string
      birthDate?: string
      sex?: string
      birthPlaceCode?: string
    } = {}
    if (!insNIR) e.insNIR = t`INS number is required.`
    else if (insNIR.length !== 15) e.insNIR = t`INS number, including the control key, must contain 15 characters.`
    else if (!hasValidInsFormat(insNIR)) e.insNIR = t`INS number format is invalid.`
    else if (!hasValidInsControlKey(insNIR)) e.insNIR = t`INS control key is invalid.`
    if (!insOID) e.insOID = t`INS OID is required.`
    if (!birthSurname.trim()) e.birthSurname = t`Birth surname is required.`
    if (!birthName.trim()) e.birthName = t`Birth given name(s) are required.`
    if (!birthDate) e.birthDate = t`Birth date is required.`
    if (!sex) e.sex = t`Sex is required.`
    if (!birthPlaceCode) e.birthPlaceCode = t`Birth place code is required.`
    else if (!BIRTH_PLACE_CODE_REGEX.test(birthPlaceCode)) e.birthPlaceCode = t`Birth place code must contain 5 characters.`
    return e
  }, [birthDate, birthName, birthPlaceCode, birthSurname, insNIR, insOID, sex])

  const hasValidationErrors = Object.keys(errors).length > 0
  const canSubmit = !hasValidationErrors && emitter !== ''

  const reset = () => {
    setEmitter('')
    setInsNIR('')
    setInsOID('')
    setBirthSurname('')
    setBirthName('')
    setBirthDate('')
    setSex('')
    setBirthPlaceCode('')
    setStep('form')
    setCandidates([])
    setCollisionPatient(null)
    setSelectedId('')
    setReceivedData(null)
    setRefusal(null)
    setSkippedReason(null)
    setSearchFailed(false)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    if (!establishmentId) {
      enqueueSnackbar(t`No active establishment in your session.`, { variant: 'error' })
      return
    }
    const patientGender = sex as NonNullable<PatientProfileFormData['gender']>
    const { patientData, ingestTraits } = buildExternalIdentityData({
      insNIR,
      insOID,
      birthSurname,
      birthName,
      birthDate,
      gender: patientGender,
      birthPlaceCode,
    })
    setReceivedData(patientData)

    const reconciliation = await reconcile(patientData, emitterName)
    if (reconciliation.status === 'ERROR') {
      setSearchFailed(true)
      setStep('reconciliation')
      return
    }
    if (reconciliation.status === 'CANDIDATES') {
      setCandidates(reconciliation.candidates)
      setCollisionPatient(reconciliation.collisionPatient)
      const exact = reconciliation.candidates.filter((c) => c.kind === 'EXACT')
      setSelectedId(exact.length === 1 ? exact[0].patient._id : '')
      setStep('reconciliation')
      return
    }

    if (isInsAlreadyVerifiedLocally(patientData, reconciliation.collisionPatient)) {
      setCollisionPatient(reconciliation.collisionPatient)
      setSkippedReason('COLLISION')
      setStep('reconciliation')
      return
    }

    if (isTrustedEmitter) {
      onVerified?.({ ...patientData, insEmitter: emitter })
      enqueueSnackbar(t`Identity received from a trusted actor`, { variant: 'success' })
      handleClose()
      return
    }

    if (!canRunInsVerification) {
      enqueueSnackbar(t`INSi connector unavailable. Check the DMP Connect connection.`, { variant: 'error' })
      return
    }
    const patientFullName = `${patientData.birthSurname} ${patientData.birthFirstName}`.trim()
    const result = await runInsVerification(patientData, { patientFullName })
    if (result === 'ERROR') {
      enqueueSnackbar(t`INSi verification failed`, { variant: 'error' })
      return
    }
    if (result === 'OK') {
      onVerified?.({ ...patientData, insEmitter: UNTRUSTED_EMITTER_NAME })
      enqueueSnackbar(t`Identity verified by INSi — status « Recovered »`, { variant: 'success' })
    } else {
      let quarantined = false
      try {
        await ingestExternalIdentity({
          establishmentId,
          emitter: emitterName,
          insNIR: patientData.insNIR ?? insNIR,
          insOID: patientData.insOID ?? insOID,
          traits: { ...ingestTraits, sex: ingestTraits.sex === 'I' ? 'U' : ingestTraits.sex },
        }).unwrap()
        quarantined = true
      } catch (error) {
        console.error('[IngestExternalIdentityDialog] Failed to quarantine the received identity:', error)
      }

      onVerified?.({ ...patientData, insNIR: '', insOID: '', insEmitter: UNTRUSTED_EMITTER_NAME })
      enqueueSnackbar(
        quarantined
          ? t`INS not validated by INSi — the received data was placed in the worklist for review and was not integrated. The identity can be created with the « Provisional » status, without an INS registration number.`
          : t`INS not validated by INSi — « Provisional » status (no INS registration number). Adding it to the worklist failed.`,
        { variant: 'warning', persist: true }
      )
    }
    handleClose()
  }

  const handleVerifyAndUpdate = async () => {
    if (!selectedCandidate || !receivedData || blocker) return
    setRefusal(null)

    if (isInsAlreadyVerifiedLocally(receivedData, selectedCandidate.patient)) {
      setSkippedReason('ALREADY_VERIFIED')
      return
    }

    const result = await runInsVerification(receivedData, { patientId: selectedCandidate.patient._id })

    if (result === 'ERROR') {
      setRefusal('ERROR')
      return
    }
    if (result === 'KO') {
      setRefusal('KO')
      enqueueSnackbar(t`INSi returned KO — the existing identity has not been updated`, { variant: 'error' })
      return
    }

    try {
      await updatePatient({
        ...buildInsOnlyUpdateDto(selectedCandidate.patient, receivedData, emitterName),
        id: selectedCandidate.patient._id,
      }).unwrap()
      enqueueSnackbar(t`The existing identity has been updated with the received INS matricule`, { variant: 'success' })
      const updatedId = selectedCandidate.patient._id
      handleClose()
      onUpdated?.(updatedId)
    } catch (error) {
      console.error('[IngestExternalIdentityDialog] Failed to update the existing identity:', error)
      enqueueSnackbar(t`The existing identity could not be updated`, { variant: 'error' })
    }
  }

  const handleCreateInstead = () => {
    if (!receivedData) return
    onVerified?.({ ...receivedData, insEmitter: emitterName })
    enqueueSnackbar(t`Identity received — complete the creation form`, { variant: 'info' })
    handleClose()
  }

  if (step === 'reconciliation') {
    return (
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (isBusy || reason === 'backdropClick') return
          handleClose()
        }}
        disableEscapeKeyDown={isBusy}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { maxWidth: 720 } }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
          <Trans>Reconcile with an existing identity</Trans>
        </DialogTitle>
        <DialogContent sx={{ pt: '24px !important' }}>
          {searchFailed ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              <Trans>
                The identity repository could not be searched, so it is unknown whether this user already exists. No identity has been created or updated.
              </Trans>
            </Alert>
          ) : (
            receivedData && (
              <ExternalIdentityReconciliationStep
                received={receivedData}
                emitter={emitterName}
                candidates={candidates}
                selectedId={selectedId}
                onSelect={setSelectedId}
                blocker={blocker}
                refusal={refusal}
                skippedReason={skippedReason}
                disabled={isBusy}
              />
            )
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="neutral" onClick={handleClose} disabled={isBusy}>
            <Trans>Close</Trans>
          </Button>
          {!searchFailed &&
            !skippedReason &&
            (selectedId === CREATE_INSTEAD ? (
              <Button variant="contained" onClick={handleCreateInstead} disabled={isBusy}>
                <Trans>Create a new identity</Trans>
              </Button>
            ) : (
              <Button variant="contained" onClick={handleVerifyAndUpdate} disabled={isBusy || !selectedCandidate || Boolean(blocker)}>
                <Trans>Verify and update</Trans>
              </Button>
            ))}
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" PaperProps={{ sx: { maxWidth: 720 } }}>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <Trans>Receive external identity</Trans>
      </DialogTitle>
      <DialogContent sx={{ pt: '24px !important' }}>
        <Stack spacing={2.25} sx={{ mt: 1 }}>
          {!establishmentId && (
            <Alert severity="error">
              <Trans>No active establishment in your session.</Trans>
            </Alert>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              columnGap: 2,
              rowGap: 4,
              alignItems: 'start',
            }}
          >
            <FormControl fullWidth size="small" required sx={{ ...REQUIRED_FIELD_SX, gridColumn: { sm: '1 / -1' } }}>
              <InputLabel id="ins-ingest-emitter-label">
                <Trans>Identity issuer</Trans>
              </InputLabel>
              <Select labelId="ins-ingest-emitter-label" label={t`Identity issuer`} value={emitter} onChange={(e) => setEmitter(e.target.value)}>
                <MenuItem value="" disabled>
                  <em>—</em>
                </MenuItem>
                {trustedActors.map((actor) => (
                  <MenuItem key={actor._id} value={actor.name}>
                    {actor.name} — <Trans>contrat de confiance</Trans>
                  </MenuItem>
                ))}
                <MenuItem value={UNTRUSTED_EMITTER}>
                  <Trans>Issuer without a trust agreement</Trans>
                </MenuItem>
              </Select>
              <FormHelperText>
                {isTrustedEmitter ? (
                  <Trans>Trusted actor: the identity will be created as « Qualified » with the INS registration number.</Trans>
                ) : emitter === UNTRUSTED_EMITTER ? (
                  <Trans>Without a trust agreement: an INSi call is made — OK ⇒ « Recovered », KO ⇒ « Provisional ».</Trans>
                ) : (
                  ' '
                )}
              </FormHelperText>
            </FormControl>

            <TextField
              label={t`INS number`}
              value={insNIR}
              onChange={(e) => setInsNIR(e.target.value.replace(/\s/g, '').toUpperCase())}
              error={!!insNIR && !!errors.insNIR}
              helperText={insNIR && errors.insNIR ? errors.insNIR : ' '}
              size="small"
              fullWidth
              required
              inputProps={{ maxLength: 15 }}
              sx={REQUIRED_FIELD_SX}
            />

            <FormControl fullWidth size="small" required sx={REQUIRED_FIELD_SX}>
              <InputLabel id="ins-ingest-oid-label">
                <Trans>OID</Trans>
              </InputLabel>
              <Select labelId="ins-ingest-oid-label" label={t`OID`} value={insOID} onChange={(e) => setInsOID(e.target.value)}>
                <MenuItem value="" disabled>
                  <em>—</em>
                </MenuItem>
                {INS_OID_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText> </FormHelperText>
            </FormControl>

            <TextField
              label={t`Birth surname`}
              value={birthSurname}
              onChange={(e) => setBirthSurname(normalizeUppercaseInput(e.target.value))}
              error={!!birthSurname && !!errors.birthSurname}
              helperText={birthSurname && errors.birthSurname ? errors.birthSurname : ' '}
              size="small"
              fullWidth
              required
              sx={REQUIRED_FIELD_SX}
            />
            <TextField
              label={t`Birth given name(s)`}
              value={birthName}
              onChange={(e) => setBirthName(normalizeUppercaseInput(e.target.value))}
              error={!!birthName && !!errors.birthName}
              helperText={birthName && errors.birthName ? errors.birthName : ' '}
              size="small"
              fullWidth
              required
              sx={REQUIRED_FIELD_SX}
            />
            <TextField
              label={t`Birth date`}
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              size="small"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              sx={REQUIRED_FIELD_SX}
            />
            <FormControl fullWidth size="small" required sx={REQUIRED_FIELD_SX}>
              <InputLabel id="ins-ingest-sex-label">
                <Trans>Sex</Trans>
              </InputLabel>
              <Select labelId="ins-ingest-sex-label" label={t`Sex`} value={sex} onChange={(e) => setSex(e.target.value)}>
                <MenuItem value="" disabled>
                  <em>—</em>
                </MenuItem>
                <MenuItem value="M">M</MenuItem>
                <MenuItem value="F">F</MenuItem>
              </Select>
              <FormHelperText> </FormHelperText>
            </FormControl>
            <TextField
              label={t`Birth place code (INSEE code)`}
              value={birthPlaceCode}
              onChange={(e) => setBirthPlaceCode(e.target.value.replace(/\s/g, '').toUpperCase())}
              error={!!birthPlaceCode && !!errors.birthPlaceCode}
              helperText={birthPlaceCode && errors.birthPlaceCode ? errors.birthPlaceCode : ' '}
              size="small"
              fullWidth
              required
              inputProps={{ maxLength: 5 }}
              sx={{ ...REQUIRED_FIELD_SX, gridColumn: { sm: '1 / -1' } }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="neutral" onClick={handleClose}>
          <Trans>Cancel</Trans>
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || isBusy}>
          <Trans>Receive the identity</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default IngestExternalIdentityDialog
